import { Data, Shape, Annotations, YAxisName, Config, LayoutAxis } from "plotly.js";
import { secondsToMinutesAndSeconds } from "../../../../helpers/MathHelpers";
import { hexToRGBA } from "../../../../helpers/ColorHelpers";
import { AddedDamageDisplayForDiagram } from "../../../../services/damage/Damage";
import {
  CodeDetection,
  PredictionGroup,
  VideoAnalysisService,
} from "../../../../services/inspection/VideoAnalysisService";
import {
  VideoPlayerService,
  videoPlayerService,
} from "../../../../services/video-player/VideoPlayerService";
import { translationService } from "../../../../services/translation/TranslationService";
import { LaneForCodesAvailableForDetection } from "./CharacterizationsPlot";

export const getProgressLine: (currentTime: number) => Partial<Shape> = (currentTime) => {
  const currentPosition = currentTime;
  return {
    name: "vertical line",
    type: "line",
    xref: "x",
    yref: "paper",
    x0: currentPosition,
    y0: 0,
    x1: currentPosition,
    y1: 1,
    line: {
      color: "rgb(33,33,33)",
      dash: "solid",
      width: 2,
    },
  };
};

export const getOverallConfidenceLine: (
  videoAnalysisService: VideoAnalysisService
) => Partial<Shape> = (videoAnalysisService) => {
  return {
    name: "horizontal line",
    type: "line",
    xref: "x",
    yref: "paper",
    x0: 0,
    y0: videoAnalysisService.analysisOverallConfidence,
    x1: videoPlayerService.videoDuration,
    y1: videoAnalysisService.analysisOverallConfidence,
    line: {
      color: "#8cc34b",
      dash: "solid",
      width: 2,
    },
  };
};

export const getHoveredLine: (
  videoDuration: number,
  mousePositionX: number | null
) => Partial<Shape> | undefined = (videoDuration, mousePositionX) => {
  const isHovered = mousePositionX && mousePositionX > 0 && mousePositionX < videoDuration;
  if (!isHovered) return undefined;

  const currentPosition = mousePositionX;
  return {
    name: "vertical line",
    type: "line",
    xref: "x",
    yref: "paper",
    x0: currentPosition,
    y0: 0,
    x1: currentPosition,
    y1: 1,
    line: {
      color: "rgba(33,33,33,0.2)",
      dash: "dash",
      width: 2,
    },
  };
};

export const getProgressOrHoveredAnnotation: (
  currentTime: number,
  videoDuration: number,
  videoAnalysisService: VideoAnalysisService,
  damageDetails: AddedDamageDisplayForDiagram[],
  mousePositionX: number | null
) => Partial<Annotations>[] = (
  currentTime,
  videoDuration,
  videoAnalysisService,
  damageDetails,
  mousePositionX
) => {
  const isHovered =
    mousePositionX &&
    mousePositionX > 0 &&
    mousePositionX < videoDuration &&
    Math.round(mousePositionX) !== currentTime;

  const tooltipShifter = (tooltipText: string, position: number, halfLetterSize: number = 4) =>
    position < tooltipText.length * halfLetterSize
      ? tooltipText.length * halfLetterSize - position
      : position > videoDuration - tooltipText.length * halfLetterSize
      ? videoDuration - tooltipText.length * halfLetterSize - position
      : 0;

  const detectionText = (position: number) => {
    const filteredGroups = videoAnalysisService.detectedMainCodes
      .filter((m) => m.isVisible)
      .map((m) => m.code);
    const detections = videoAnalysisService.characterizationAtTime(position);
    const text = Object.keys(detections)
      .filter((c) => filteredGroups.indexOf(c) !== -1)
      .reduce((p, c, i) => {
        const characterization = Object.keys(detections[c]).reduce(
          (p, key, i) => `${p}${i === 0 ? "" : ", "}${key}:${detections[c][key]}`,
          ""
        );
        return `${p}${i === 0 ? "" : " | "}${c}${characterization ? ` (${characterization})` : ""}`;
      }, "");
    return text;
  };

  const progressText = (position: number) => {
    const addedDamage = damageDetails.find((d) => Number(d.videoPosition) === position);
    const text = `${secondsToMinutesAndSeconds(position)}${
      addedDamage ? ` | ${addedDamage.mainCode}` : ""
    }`;
    return text;
  };

  const getTopAnnotation = (position: number, color: string): Partial<Annotations> => ({
    xref: "x",
    yref: "paper",
    x: position,
    y: 1.1,
    text: detectionText(position),
    showarrow: false,
    font: {
      color: "white",
      size: 14,
    },
    borderwidth: 1,
    borderpad: 4,
    bgcolor: color,
    visible: detectionText(position) !== "",
    xshift: tooltipShifter(detectionText(position), position),
  });

  const getbottomAnnotation = (position: number, color: string): Partial<Annotations> => ({
    xref: "x",
    yref: "paper",
    x: position,
    y: -0.1,
    text: progressText(position),
    showarrow: false,
    font: {
      color: "white",
      size: 14,
    },
    borderwidth: 1,
    borderpad: 4,
    bgcolor: color,
    xshift: tooltipShifter(progressText(position), position),
  });

  return isHovered
    ? [
        getTopAnnotation(currentTime, hexToRGBA("#000000")),
        getbottomAnnotation(currentTime, hexToRGBA("#000000")),
        getTopAnnotation(Math.round(mousePositionX), hexToRGBA("#dcdcdc")),
        getbottomAnnotation(Math.round(mousePositionX), hexToRGBA("#dcdcdc")),
      ]
    : [
        getTopAnnotation(currentTime, hexToRGBA("#000000")),
        getbottomAnnotation(currentTime, hexToRGBA("#000000")),
      ];
};

export const getPlotDataForAddedDamages: (
  damageDetails: AddedDamageDisplayForDiagram[],
  videoAnalysisService: VideoAnalysisService,
  yaxis: YAxisName
) => Data = (damageDetails, videoAnalysisService, yaxis) => {
  return {
    type: "scatter",
    name: "Damage",
    xaxis: "x",
    yaxis,
    x: damageDetails.map((details) => details.videoPosition),
    y: damageDetails.map(() => 0.5),
    mode: "markers",
    hoverinfo: "none",
    marker: {
      size: 12,
      color: damageDetails.map(
        (details) => videoAnalysisService.mainCodeColors[details.mainCode] ?? "#000000"
      ),
      line: {
        width: 2,
        color: "#ffffff",
      },
    },
  };
};

export const getDetectedDamageBarsForMainCode: (
  currentX: number[],
  index: number,
  group: PredictionGroup,
  secondaryCodePresent: string,
  plotColor: any,
  i: number
) => Data = (currentX, index, group, secondaryCodePresent, plotColor, i) => {
  return {
    x: currentX,
    y: currentX.map(() => 1),
    yaxis: `y${index + 2}`,
    type: "scatter",
    fill: "tozeroy",
    mode: "lines",
    hoverinfo: "none",
    text: `${group.mainCode} ${secondaryCodePresent} ${group.predictions[i].secondaryCode}`,
    name: `${group.mainCode} ${secondaryCodePresent} ${group.predictions[i].secondaryCode}`,
    fillcolor: plotColor,
    line: {
      color: plotColor,
    },
  };
};

export const getTimestamps: (
  videoDuration: number,
  maxLength: number,
  commonDivider: number
) => number[] = (videoDuration, maxLength, commonDivider) => {
  if (videoDuration <= commonDivider) {
    return [0, videoDuration];
  }

  const timestamps: number[] = [];
  let length = 0;

  for (let i = maxLength; i >= 2; i--) {
    if (videoDuration / i > commonDivider) {
      length = i;
      break;
    }
  }

  const interval = Math.floor(videoDuration / length / commonDivider) * commonDivider;

  for (let i = 0; i < length; i++) {
    timestamps.push(i * interval);
  }

  if (length * interval < videoDuration) timestamps.push(videoDuration);
  else timestamps[length - 1] = videoDuration;

  return timestamps;
};

export const getPlotDataForPauses: (
  videoAnalysisService: VideoAnalysisService,
  videoPlayerService: VideoPlayerService
) => Data = (videoAnalysisService, videoPlayerService) => {
  const x = videoAnalysisService.allTimestamps.filter(
    (timestamp) => Number(timestamp) < videoPlayerService.videoDuration
  );

  return {
    type: "scatter",
    name: "Pause",
    x,
    y: videoAnalysisService.pauses,
    fill: "tozeroy",
    mode: "lines",
    hoverinfo: "none",
    line: {
      width: 1,
    },
    marker: {
      color: "#CCCCCC",
    },
  };
};

export const getPlotDataForDetectedDamages: (
  videoAnalysisService: VideoAnalysisService,
  videoPlayerService: VideoPlayerService,
  detectedMainCode: CodeDetection
) => Data = (videoAnalysisService, videoPlayerService, detectedMainCode) => {
  const x = videoAnalysisService.allTimestamps.filter(
    (timestamp) => Number(timestamp) < videoPlayerService.videoDuration
  );
  const y = videoAnalysisService.getPredictionsByCode(detectedMainCode.code);

  return {
    type: "scatter",
    name: detectedMainCode.code,
    x,
    y,
    fill: "tozeroy",
    mode: "lines",
    hoverinfo: "none",
    line: {
      width: 1,
    },
    marker: {
      color: detectedMainCode.colorCode,
    },
  };
};

export const getBackgroundForDetectedDamages: (
  videoPlayerService: VideoPlayerService
) => Partial<Shape> = (videoPlayerService) => {
  return {
    type: "rect",
    layer: "below",
    xref: "x",
    yref: "paper",
    x0: 0,
    y0: 0,
    x1: videoPlayerService.videoDuration,
    y1: 0.07,
    fillcolor: "#212121",
    line: {
      width: 0,
    },
  };
};

export const getBackgroundBar: (
  videoPlayerService: VideoPlayerService,
  index: number,
  colorCode: string,
  lightRatio?: number
) => Partial<Shape> = (videoPlayerService, index, colorCode, lightRatio = 1) => {
  return {
    type: "rect",
    xref: "x",
    yref: `y${index + 2}` as YAxisName,
    line: { width: 0 },
    fillcolor: hexToRGBA(colorCode, lightRatio),
    layer: "below",
    x0: 0,
    y0: 0,
    x1: videoPlayerService.videoDuration,
    y1: 1,
  };
};

export const getSeparatorLine: (
  videoPlayerService: VideoPlayerService,
  index: number,
  lanes: LaneForCodesAvailableForDetection[],
  position: "below" | "above"
) => Partial<Shape> = (videoPlayerService, index, lanes, position) => {
  const domain = lanes.filter((lane) => lane.laneIndex === index)[0].yAxisDomain;
  const separatorWidth = 0.005;

  return {
    layer: "above",
    type: "rect",
    xref: "x",
    yref: `paper`,
    line: { width: 0 },
    fillcolor: "white",
    x0: 0,
    y0: position === "below" ? domain.start - separatorWidth / 2 : domain.end - separatorWidth / 2,
    x1: videoPlayerService.videoDuration,
    y1: position === "below" ? domain.start + separatorWidth / 2 : domain.end + separatorWidth / 2,
  };
};

const getOpacityForLabel = (
  mousePositionX: number | null,
  opacityIncreaseStartPoint: number,
  opacityIncreaseEndPoint: number,
  minOpacity: number,
  maxOpacity: number
) => {
  if (mousePositionX === null || mousePositionX > opacityIncreaseStartPoint) return maxOpacity;
  if (mousePositionX < opacityIncreaseEndPoint) return minOpacity;

  const opacityRange = maxOpacity - minOpacity;
  const position = mousePositionX - opacityIncreaseEndPoint;
  const positionRange = opacityIncreaseStartPoint - opacityIncreaseEndPoint;
  const opacityIncrease = (position / positionRange) * opacityRange;

  return minOpacity + opacityIncrease;
};

export const getLabel: (
  videoAnalysisService: VideoAnalysisService,
  index: number,
  laneIndex: number,
  filteredGroups: PredictionGroup[],
  mousePositionX: number | null
) => Partial<Annotations> = (
  videoAnalysisService,
  index,
  laneIndex,
  filteredGroups,
  mousePositionX
) => {
  const mainCode = filteredGroups[index].mainCode;
  const colorCode = videoAnalysisService.mainCodeColors[mainCode];

  return {
    xref: "paper",
    xanchor: "left",
    yref: `y${laneIndex + 2}` as YAxisName,
    align: "left",
    x: 0.005,
    y: 0.5,
    text: `${mainCode}: ${videoAnalysisService.getSecondaryCodeName(filteredGroups[index])}`,
    showarrow: false,
    font: {
      color: colorCode,
      size: 11,
    },
    bgcolor: hexToRGBA(colorCode, undefined, 0.15),
    bordercolor: colorCode,
    width: 50,
    height: 11,
    opacity: getOpacityForLabel(mousePositionX, 500, 50, 0.25, 1),
  };
};

export const getYaxis: (
  index: number,
  lanes: LaneForCodesAvailableForDetection[]
) => Partial<LayoutAxis> = (index, lanes) => {
  const domain = lanes.filter((lane) => lane.laneIndex === index)[0].yAxisDomain;
  return {
    showticklabels: false,
    domain: [domain.start, domain.end],
    range: [0, 1],
    visible: true,
    zeroline: false,
    showgrid: false,
  };
};

export const getLayoutConfiguration: () => Partial<Config> = () => {
  return {
    doubleClick: false,
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    locale: translationService.language,
    locales: {
      de: {
        moduleType: "locale",
        dictionary: {
          "Download plot as a png": translationService.translate("analysis.plot.download-plot"),
          "Zoom in": translationService.translate("analysis.plot.zoom-in"),
          "Zoom out": translationService.translate("analysis.plot.zoom-out"),
          "Reset axes": translationService.translate("analysis.plot.reset"),
        },
      },
    },
    modeBarButtons: [["toImage", "zoomIn2d", "zoomOut2d", "resetScale2d"]],
  };
};
